'use client'

import { useRef, useEffect } from 'react'
import {
  X,
  PanelLeft,
  PanelRight,
  PictureInPicture2,
  Plane,
  CalendarPlus,
  GraduationCap,
  ChevronRight,
  type LucideIcon,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { SoloMark } from '@/components/brand/solo-mark'
import { BigglesMessage, type ChatMessage } from './biggles-message'
import { BigglesInput } from './biggles-input'
import { useBigglesWindow } from './use-biggles-window'

/**
 * Icons are Lucide, NOT emoji.
 *
 * These three chips used to be 🛩️ 📅 📚 — the only emoji anywhere in the app's
 * chrome. Emoji are raster glyphs supplied by the OS, so they cannot take the
 * theme (fixed colours on every palette), cannot match the stroke weight of
 * the ~200 Lucide icons around them, and render as a completely different
 * picture per platform: the calendar Wayne was looking at is Segoe UI Emoji's
 * blue-and-white block, which no Mac user has ever seen. Lucide inherits
 * currentColor and follows the palette for free.
 */
type QuickPrompt =
  | { icon: LucideIcon; label: string; prompt: string }
  // `reply` chips post a canned Biggles line locally — no model call, no
  // delay. The student's answer is the first thing that reaches Claude.
  | { icon: LucideIcon; label: string; reply: string }

const QUICK_PROMPTS: QuickPrompt[] = [
  { icon: Plane, label: 'Next flight', prompt: "When's my next flight?" },
  { icon: CalendarPlus, label: 'Book a lesson', prompt: 'I want to book a flight lesson' },
  // "Message CFI" used to live here; chat is disabled in the app, so it promised
  // something Biggles could not do. Ground school is something he can.
  {
    icon: GraduationCap,
    label: 'Quiz me',
    reply:
      "What would you like me to quiz you on? Name a PHAK chapter — airspace, weather, aerodynamics, systems, weight and balance, navigation, aeromedical factors — or say **surprise me** and I'll pick.",
  },
]

interface BigglesPanelProps {
  isOpen: boolean
  onClose: () => void
  messages: ChatMessage[]
  onSend: (message: string) => void
  onLocalReply: (text: string) => void
  isLoading: boolean
  userName: string | null
}

export function BigglesPanel({
  isOpen,
  onClose,
  messages,
  onSend,
  onLocalReply,
  isLoading,
  userName,
}: BigglesPanelProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const win = useBigglesWindow()

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  if (!isOpen || !win.layout || !win.style) return null

  const mode = win.layout.mode
  const floating = mode === 'floating'

  return (
    <div
      role="dialog"
      aria-label="SOLO assistant"
      data-biggles
      data-mode={mode}
      style={win.style}
      className={cn(
        'z-50 flex flex-col bg-card text-card-foreground border-border overflow-hidden',
        'max-w-[calc(100vw-1rem)]',
        floating
          ? 'rounded-xl border shadow-floating'
          : 'shadow-floating',
        mode === 'docked-left' && 'border-r',
        mode === 'docked-right' && 'border-l',
        win.isDragging ? 'select-none' : 'transition-[left,top,width,height,border-radius] duration-base ease-out'
      )}
    >
      {/* Header — the drag handle when floating */}
      <div
        onPointerDown={win.onHeaderPointerDown}
        onDoubleClick={floating ? win.resetPosition : undefined}
        title={floating ? 'Drag to move · double-click to reset' : undefined}
        className={cn(
          // Quiet chrome (2026-08-29). This was a full-bleed `bg-primary` slab
          // — ~90px of fully-saturated brand colour, which is the single thing
          // that dated the panel: it reads as a 2014 support widget, and it
          // fought every palette (electric lavender under Cosmic Night, with
          // the warm avatar stranded on top of it). Surface + a hairline rule
          // instead; the avatar and the send button carry the brand now.
          'relative shrink-0 px-4 py-3 border-b border-border bg-muted/40',
          floating && (win.isDragging ? 'cursor-grabbing' : 'cursor-grab')
        )}
      >
        <div className="relative flex items-center justify-between gap-2">
          <div className="flex items-center gap-3 min-w-0">
            {/* The helmet reads well below this size (24px verified), but the
                44px slot predates it and there is no reason to shrink. */}
            <div className="relative shrink-0">
              <SoloMark className="h-11 w-11" />
              <div
                aria-hidden
                // --success, not --series-3. A chart categorical hue was
                // standing in for "online" here; re-picking chart colours for
                // chart reasons would have silently changed a status light.
                className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-success ring-2 ring-card"
              />
            </div>
            <div className="min-w-0">
              <h3 className="font-semibold text-sm leading-tight text-foreground">SOLO</h3>
              <p className="text-xs text-muted-foreground truncate">Flight School AI</p>
            </div>
          </div>

          <div className="flex items-center gap-0.5 shrink-0">
            <ModeButton
              active={mode === 'docked-left'}
              label="Dock to left side"
              onClick={() => win.setMode(mode === 'docked-left' ? 'floating' : 'docked-left')}
            >
              <PanelLeft className="h-4 w-4" />
            </ModeButton>
            <ModeButton
              active={floating}
              label="Float as a window"
              onClick={() => win.setMode('floating')}
            >
              <PictureInPicture2 className="h-4 w-4" />
            </ModeButton>
            <ModeButton
              active={mode === 'docked-right'}
              label="Dock to right side"
              onClick={() => win.setMode(mode === 'docked-right' ? 'floating' : 'docked-right')}
            >
              <PanelRight className="h-4 w-4" />
            </ModeButton>
            <div aria-hidden className="w-px h-5 mx-1 bg-border" />
            <ModeButton label="Close SOLO" onClick={onClose}>
              <X className="h-4 w-4" />
            </ModeButton>
          </div>
        </div>
      </div>

      {/* Messages area */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
        {messages.length === 0 ? (
          <WelcomeScreen onPrompt={onSend} onReply={onLocalReply} disabled={isLoading} userName={userName} />
        ) : (
          messages.map((msg) => (
            <BigglesMessage key={msg.id} message={msg} />
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <BigglesInput onSend={onSend} disabled={isLoading} />

      {/* Resize grip — floating only */}
      {floating && (
        <div
          role="separator"
          aria-label="Resize SOLO window"
          aria-orientation="horizontal"
          onPointerDown={win.onResizeHandlePointerDown}
          className="absolute bottom-0 right-0 h-4 w-4 cursor-nwse-resize"
        >
          <svg aria-hidden viewBox="0 0 16 16" className="h-full w-full text-muted-foreground">
            <circle cx="13" cy="13" r="1" fill="currentColor" />
            <circle cx="9" cy="13" r="1" fill="currentColor" />
            <circle cx="13" cy="9" r="1" fill="currentColor" />
          </svg>
        </div>
      )}
    </div>
  )
}

function ModeButton({
  active = false,
  label,
  onClick,
  children,
}: {
  active?: boolean
  label: string
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      aria-pressed={active || undefined}
      title={label}
      className={cn(
        'h-8 w-8 rounded-md flex items-center justify-center transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        active
          ? 'bg-accent text-accent-foreground'
          : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
      )}
    >
      {children}
    </button>
  )
}

function WelcomeScreen({
  onPrompt,
  onReply,
  disabled,
  userName,
}: {
  onPrompt: (text: string) => void
  onReply: (text: string) => void
  disabled: boolean
  userName: string | null
}) {
  const greeting = userName ? `Hi, ${userName}!` : 'Hey there, pilot!'

  return (
    // The avatar at 64px is what fills the empty state. Three small chips at
    // the top of a 500px void read as an unfinished screen; something has to
    // anchor the composition, and the character is the one thing here worth
    // looking at.
    <div className="flex flex-col items-center px-2 pt-6">
      {/* No hero mark here (Wayne, 2026-08-30). The 96px Biggles was the one
          placement where the line art was unambiguously good, and it still
          made the welcome screen feel like a splash page rather than a tool.
          He is already in the panel; he does not need to be introduced to a
          portrait. The character now lives only in the header. */}
      <h2 className="text-lg font-semibold mb-1 text-foreground">{greeting}</h2>
      <p className="text-[13px] text-muted-foreground text-center mb-5 max-w-[280px] leading-relaxed">
        I&apos;m SOLO, your flight school AI. Ask me about your schedule, aircraft, training, or book a lesson!
      </p>

      {/* A vertical list, not a 3-up grid. At the floating panel's ~380px the
          grid gave each chip ~85px, so "Book a lesson" wrapped to two lines of
          11px text. Full-width rows fit the label on one line and leave room
          for the icon to sit beside it rather than above it. */}
      <div className="w-full space-y-2">
        {QUICK_PROMPTS.map((item) => (
          <button
            key={item.label}
            type="button"
            onClick={() => ('reply' in item ? onReply(item.reply) : onPrompt(item.prompt))}
            disabled={disabled}
            className="group w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border border-border bg-muted/40 hover:bg-accent hover:text-accent-foreground hover:border-ring/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-colors disabled:opacity-50"
          >
            <item.icon className="h-4 w-4 shrink-0 text-muted-foreground group-hover:text-accent-foreground transition-colors" />
            <span className="flex-1 text-left text-[13px] font-medium leading-tight">
              {item.label}
            </span>
            <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground/50 group-hover:text-accent-foreground transition-colors" />
          </button>
        ))}
      </div>
    </div>
  )
}
